import React, { useContext, useState } from 'react';
import { OrderInput, OrderUtil } from '../../../../../types/order';
import { ActionModal } from '../../../../components/Modal';
import { TranslationKey } from '../../../../../types/translation/TranslationKey';
import { t } from '../../../../../types/translation/Translator';
import { DropdownSearchSelect } from '../../../../components/Dropdown';
import { CustomFieldContext } from '../../../../../context/CustomFieldContext';
import { CustomField, CustomFieldEntityType } from '../../../../../types/custom_field';
import { TextInput } from '../../../../components/TextInput';
import { Button, ButtonVariant } from '../../../../components/Button';
import CustomFieldInput from '../../../Common/CustomFieldInput';
import { cloneDeep } from '@apollo/client/utilities';
import { UserContext } from '../../../../../context/UserContext';
import { v4 } from 'uuid';
import { ContactContext } from '../../../../../context/ContactContext';
import { ProductMasterDataContext } from '../../../../../context/ProductMasterDataContext';

interface AddOrderCustomFieldModalProps {
  open: boolean;
  setOpen: (value: boolean) => void;

  order: OrderInput;
  setOrder: (order: OrderInput) => void;
}

export default function AddOrderCustomFieldModal({ open, setOpen, order, setOrder }: AddOrderCustomFieldModalProps) {
  const { customFields } = useContext(CustomFieldContext);
  const { companyUsers } = useContext(UserContext);
  const { contacts } = useContext(ContactContext);
  const { productMasterData } = useContext(ProductMasterDataContext);

  const [input, setInput] = useState<OrderInput>(new OrderInput(order));
  const [customField, setCustomField] = useState<CustomField | undefined>(undefined);

  const entitySubtype = OrderUtil.typeToEntitySubType(input.type);
  const items = [...customFields.values()].filter(
    v =>
      v.entityType === CustomFieldEntityType.order &&
      (!v.entitySubtype || v.entitySubtype === entitySubtype) &&
      v.manual,
  );

  const footer = (
    <div className='flex justify-end gap-2'>
      <Button
        text={t(TranslationKey.cancel).singular.label}
        variant={ButtonVariant.secondary}
        onClick={() => setOpen(false)}
      />
      <Button
        text={t(TranslationKey.add).singular.label}
        disabled={!customField || !customField.name}
        onClick={() => {
          if (customField) setOrder(new OrderInput(input));
          setOpen(false);
        }}
      />
    </div>
  );

  return (
    <ActionModal
      footer={footer}
      title={t(TranslationKey.addCustomField).singular.label}
      open={open}
      setOpen={setOpen}
      className='max-w-[720px]'
    >
      <div className='flex flex-col gap-2'>
        <DropdownSearchSelect
          value={customField}
          onSelect={v =>
            setCustomField(
              v
                ? new CustomField({
                    ...v,
                    id: v.generated ? `${v.id}::${v4()}` : v.id,
                    index: v.index + order.customFields.size + 1,
                  })
                : v,
            )
          }
          placeholder={t(TranslationKey.field).singular.label}
          label={t(TranslationKey.field).singular.label}
          items={items}
          textBuilder={item => item.name}
        />
        {customField && (
          <>
            <TextInput
              label={t(TranslationKey.name).singular.label}
              value={customField.name}
              onChange={v => {
                customField.name = v;
                setCustomField(cloneDeep(customField));
              }}
            />
            <CustomFieldInput
              item={customField}
              value={input.customFields.get(customField.id)?.value}
              entity={input}
              onChange={v => setInput(new OrderInput(v))}
              users={companyUsers}
              contacts={contacts}
              productMasterData={productMasterData}
            />
          </>
        )}
      </div>
    </ActionModal>
  );
}
